import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useUrlLocalizer, useTranslator } from 'tradera-lang';
import { Analytics } from 'packages';
import { DialogModal } from 'components';
import { ViewItemCarouselZoom } from 'tradera-components/carousels/view-item-carousel';
import { LocalLoader } from 'tradera-components/loader';
import HeartIcon from '@tradera/blueprint/theme/icons2/heart.svg?jsx';
import HeartFillIcon from '@tradera/blueprint/theme/icons2/heart-fill.svg?jsx';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';

import uiActions from 'tradera-state/ui/actions';
import itemActions from 'tradera-state/items/actions';

import './quick-view-modal.scss';

const QuickView = ({
    dispatch,
    itemIds,
    open,
    isLoggedIn,
    item,
    currentItem,
    loading
}) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const { t } = useTranslator();

    if (open !== 'open') {
        return null;
    }

    const { imageFormats, shortDescription, url, existsInWishList } = item;

    const handleModalDismiss = () => {
        dispatch(uiActions.closeQuickView());
    };

    const handleWishlistClick = () => {
        if (!isLoggedIn) {
            handleModalDismiss();
        }
        dispatch(itemActions.wishlist(item.itemId));
    };

    const handlePreviousClick = () => {
        const prevItem = itemIds[itemIds.indexOf(currentItem) - 1];
        dispatch(
            uiActions.openQuickView({
                itemId: prevItem,
                openedFrom: 'Quick View'
            })
        );
    };

    const handleNextClick = () => {
        const nextItem = itemIds[itemIds.indexOf(currentItem) + 1];
        dispatch(
            uiActions.openQuickView({
                itemId: nextItem,
                openedFrom: 'Quick View'
            })
        );
    };

    const urlWithTracking = url + '?itm_source=quick-view';

    const disableNext = itemIds.indexOf(currentItem) === itemIds.length - 1;
    const disablePrev = itemIds.indexOf(currentItem) < 1;

    return (
        <DialogModal
            className="quick-view position-relative px-0"
            mediaQuery={{ minWidth: 1024 }}
            onDismiss={handleModalDismiss}>
            <div
                className="container"
                onKeyDown={e => {
                    if (e.keyCode === 39 && !disableNext) {
                        handleNextClick();
                    } else if (e.keyCode === 37 && !disablePrev) {
                        handlePreviousClick();
                    }
                }}>
                <div
                    className="position-absolute"
                    style={{ top: '10px', right: '10px', zIndex: 1 }}>
                    <button
                        className="btn btn-translucent btn-round"
                        onClick={() => handleModalDismiss()}>
                        <CloseIcon className="icon icon-md" />
                    </button>
                </div>
                <div className="row">
                    {!imageFormats ? (
                        <div className="position-relative">
                            <LocalLoader />
                        </div>
                    ) : (
                        <ViewItemCarouselZoom
                            images={imageFormats}
                            pageType={'Quick View'}
                            fixedHeight={false}
                            carouselStyle={{
                                width: '100%'
                            }}
                        />
                    )}
                </div>
                <div className="row mt-4">
                    <div className="col-12 text-center">
                        <h3 className="mb-0 mx-4">{shortDescription}</h3>
                    </div>
                </div>
                <div className="row my-4">
                    <div className="col-10 mx-auto d-flex justify-content-center">
                        <button
                            className={`btn mx-2 ${
                                existsInWishList
                                    ? 'btn-success'
                                    : 'btn-outline-dark'
                            }`}
                            onClick={() => handleWishlistClick()}>
                            {existsInWishList ? (
                                <HeartFillIcon className="icon icon-danger mr-2" />
                            ) : (
                                <HeartIcon className="icon mr-2" />
                            )}
                            {existsInWishList
                                ? t('mytQuickView_savedInWishlist')
                                : t('mytQuickView_saveInWishlist')}
                        </button>
                        <a
                            href={toLocalizedUrl(urlWithTracking)}
                            className="btn btn-primary mx-2"
                            onClick={() => {
                                Analytics.trackEvent({
                                    category: 'Quick View',
                                    action: 'Navigation',
                                    label: 'View full listing'
                                });
                            }}>
                            {t('mytQuickView_viewFullListing')}
                        </a>
                    </div>
                </div>
            </div>
            {loading && (
                <div
                    className="position-absolute"
                    style={{
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0
                    }}>
                    <LocalLoader />
                </div>
            )}
        </DialogModal>
    );
};

QuickView.propTypes = {
    itemIds: PropTypes.array.isRequired,
    open: PropTypes.string,
    item: PropTypes.object,
    currentItem: PropTypes.number,
    dispatch: PropTypes.func,
    loading: PropTypes.bool
};

const mapStateToProps = state => ({
    itemIds: state.ui.quickView.itemIds,
    open: state.ui.quickView.open,
    isLoggedIn: state.member.isLoggedIn,
    item: state.items.cache[state.ui.quickView.currentItem] || null,
    currentItem: state.ui.quickView.currentItem,
    loading: state.ui.quickView.loading
});

export default connect(mapStateToProps)(QuickView);
